
import { useMemo } from 'react';
import type { PEOProvider, EnterpriseFeatures, SubscriptionTier } from '@/types/database/peo-types';

interface UsePEOEnterpriseFeaturesResult {
  hasEnterpriseAccess: boolean;
  isTrialing: boolean;
  isSubscriptionActive: boolean;
  trialEndsAt: Date | null;
  features: EnterpriseFeatures;
  subscriptionTier: SubscriptionTier;
  canAccessFeature: (feature: keyof EnterpriseFeatures) => boolean;
  upgradeRequired: boolean;
}

/**
 * Hook to check PEO enterprise feature access and subscription status
 * PEO Enterprise: 2-month free trial, then $2,000/month
 */
export const usePEOEnterpriseFeatures = (
  providerInfo: PEOProvider | null
): UsePEOEnterpriseFeaturesResult => {
  
  return useMemo(() => {
    if (!providerInfo) {
      return {
        hasEnterpriseAccess: false,
        isTrialing: false,
        isSubscriptionActive: false,
        trialEndsAt: null,
        features: getBasicFeatures(),
        subscriptionTier: 'basic',
        canAccessFeature: () => false,
        upgradeRequired: true,
      };
    }

    // Permanently enable enterprise features for the demo provider
    const isDemoProvider = providerInfo.id === 'af8fb701-853c-4599-bc17-9a12f05d64d8';
    
    const now = new Date();
    const trialEndsAt = providerInfo.trial_ends_at ? new Date(providerInfo.trial_ends_at) : null;
    const isTrialing = trialEndsAt ? now < trialEndsAt : false;
    const isSubscriptionActive = providerInfo.subscription_status === 'active';
    
    // Has enterprise access if demo provider OR admin enabled OR has active subscription OR in trial
    const hasEnterpriseAccess = 
      isDemoProvider ||
      providerInfo.enterprise_features_enabled || 
      isSubscriptionActive || 
      isTrialing;

    const features = hasEnterpriseAccess ? getEnterpriseFeatures() : getBasicFeatures();
    
    const canAccessFeature = (feature: keyof EnterpriseFeatures): boolean => {
      return hasEnterpriseAccess && features[feature];
    };

    const upgradeRequired = !hasEnterpriseAccess && !isTrialing;

    return {
      hasEnterpriseAccess,
      isTrialing,
      isSubscriptionActive,
      trialEndsAt,
      features,
      subscriptionTier: providerInfo.subscription_tier || (isDemoProvider ? 'enterprise' : 'basic'),
      canAccessFeature,
      upgradeRequired,
    };
  }, [providerInfo]);
};

function getBasicFeatures(): EnterpriseFeatures {
  return {
    userLinking: false,
    advancedAnalytics: false,
    customBranding: false,
    unlimitedApplications: false,
    prioritySupport: false,
  };
}

function getEnterpriseFeatures(): EnterpriseFeatures {
  return {
    userLinking: true,
    advancedAnalytics: true,
    customBranding: true,
    unlimitedApplications: true,
    prioritySupport: true,
  };
}
